import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { X, Sparkles, Table2, MessageSquare, Loader2, RotateCcw, ShieldCheck } from "lucide-react";
import { getLine, explainLine, testid, whenText } from "../../lib/forecastApi";
import { OriginBadge, fmtDate } from "../../lib/origin.jsx";
import { useCopilot } from "../../lib/copilot.jsx";
import DataGrid from "../datatables/DataGrid";

const KIND = { kpi: "Indicator", driver: "Driver", row: "Ranked line", chart: "Chart" };

function Explanation({ line }) {
  const [state, setState] = useState({ exp: line.explanation, busy: !line.explanation, error: null });
  const write = React.useCallback(() => {
    setState({ exp: null, busy: true, error: null });
    explainLine(line.id).then(exp => setState({ exp, busy: false, error: null }))
      .catch(e => setState({ exp: null, busy: false, error: e.message }));
  }, [line.id]);
  useEffect(() => { if (!line.explanation) write(); }, [line.explanation, write]);
  const { exp, busy, error } = state;
  return (
    <section data-testid="fc-drill-explanation">
      <div className="kpi-label flex items-center gap-1.5 mb-2"><Sparkles size={11} className="text-primary" />AI explanation</div>
      {busy && (
        <div data-testid="fc-drill-writing" className="space-y-2">
          <div className="text-[12.5px] text-muted inline-flex items-center gap-2"><Loader2 size={13} className="animate-spin text-primary" />Copilot is writing this explanation for the first time…</div>
          {[92, 100, 78, 96, 64].map((w, i) => <div key={i} className="h-2.5 rounded-sm bg-border animate-pulse" style={{ width: `${w}%` }} />)}
        </div>
      )}
      {error && (
        <div data-testid="fc-drill-explain-error" className="text-[12.5px]">
          <div className="text-critical">{error}</div>
          <button data-testid="fc-drill-explain-retry" onClick={write} className="mt-2 inline-flex items-center gap-1.5 h-8 px-3 border border-border rounded-sm hover:border-primary hover:text-primary"><RotateCcw size={12} />Try again</button>
        </div>
      )}
      {exp && (
        <>
          <div className="space-y-3 text-[13.5px] leading-relaxed" data-testid="fc-drill-explanation-text">
            {exp.text.split(/\n\s*\n/).map((p, i) => <p key={i}>{p}</p>)}
          </div>
          <div className="text-[11px] text-muted mt-2.5 font-mono-tabular" data-testid="fc-drill-written-at">
            Written {whenText(exp.generated_at)} · saved and reused until these figures change
          </div>
        </>
      )}
    </section>
  );
}

function SourceSample({ src }) {
  return (
    <section data-testid="fc-drill-source">
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <div className="kpi-label flex items-center gap-1.5"><Table2 size={11} />Data source sample</div>
        <OriginBadge origin={src.origin} source={`${src.title} · ${src.origin === "synthetic" ? "sample data" : "platform data"}`} testid="fc-drill-source-origin" />
      </div>
      <div className="text-sm font-medium" data-testid="fc-drill-source-table">{src.title}</div>
      <div className="text-[11.5px] text-muted mt-0.5">{src.about}</div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] font-mono-tabular mt-2 text-muted" data-testid="fc-drill-source-meta">
        <span><span className="text-fg">{src.rows_behind.toLocaleString()}</span> rows behind this number</span>
        <span>{src.rows_total.toLocaleString()} in table</span>
        {src.date_range && <span>{fmtDate(src.date_range[0])} – {fmtDate(src.date_range[1])}</span>}
        <span>{src.origin === "synthetic" ? "Sample" : src.origin === "actual" ? "Actual" : "Mixed"}</span>
      </div>
      <div className="text-[11px] text-subtle mt-1 inline-flex items-center gap-1"><ShieldCheck size={11} />No personal details in this table — nothing to mask.</div>
      <div className="mt-2 max-h-[300px] overflow-auto">
        <DataGrid columns={src.columns} rows={src.rows.map((r, i) => ({ ...r, _rid: i }))} masked={false} onSort={() => {}} onOpen={() => {}} />
      </div>
      <div className="text-[11px] text-muted mt-1.5">Showing {src.rows.length} of {src.rows_behind.toLocaleString()} supporting rows.</div>
    </section>
  );
}

const fmtVal = (v) => (v === null || v === undefined ? "—" : typeof v === "number" ? v.toLocaleString("en-PH", { maximumFractionDigits: 3 }) : String(v));

function Record({ facts }) {
  if (!facts?.row) return null;
  const entries = Object.entries(facts.row).filter(([k]) => !["key", "line", "data_mode"].includes(k));
  return (
    <section data-testid="fc-drill-record">
      <div className="kpi-label mb-2">This line · {facts.prediction_table?.replace(/_/g, " ")}</div>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-[12px]">
        {entries.map(([k, v]) => (
          <div key={k} className="flex justify-between gap-2 border-b border-border/50 py-1">
            <dt className="text-muted font-mono-tabular truncate">{k}</dt><dd className="font-mono-tabular text-right truncate">{fmtVal(v)}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function Body({ line, onClose }) {
  const nav = useNavigate();
  const { askCopilot } = useCopilot();
  return (
    <>
      <div className="sticky top-0 bg-bg/95 backdrop-blur-md border-b border-border px-5 py-4 z-10">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] text-primary font-semibold">{KIND[line.kind] || "Line"}</div>
            <div className="font-display text-lg font-semibold leading-snug mt-0.5" data-testid="fc-drill-title">{line.label}</div>
          </div>
          <button data-testid="fc-drill-close" onClick={onClose} aria-label="Close" className="h-8 w-8 shrink-0 inline-flex items-center justify-center border border-border rounded-sm hover:border-primary hover:text-primary"><X size={15} /></button>
        </div>
        <div className="flex items-end gap-3 mt-2">
          <div className="kpi-value text-3xl" data-testid="fc-drill-value">{line.display}</div>
          <OriginBadge origin={line.origin} source={`Forecast / Prediction Models · ${line.label}`} updatedAt={line.meta?.as_of} testid="fc-drill-origin" className="mb-1.5" />
        </div>
        {line.sub && <div className="text-[12px] text-muted mt-1">{line.sub}</div>}
      </div>
      <div className="px-5 py-5 space-y-7">
        <Explanation key={line.id} line={line} />
        <Record facts={line.facts} />
        <SourceSample src={line.source} />
      </div>
      <div className="sticky bottom-0 bg-bg/95 backdrop-blur-md border-t border-border px-5 py-3 flex flex-wrap gap-2">
        <button data-testid="fc-drill-view-source" onClick={() => { onClose(); nav(`/forecast/source?id=${encodeURIComponent(line.id)}`); }}
                className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-sm bg-primary text-primary-fg text-[12.5px] font-medium hover:opacity-90 active:scale-[0.98] transition-[opacity,transform]">
          <Table2 size={13} />View source data
        </button>
        <button data-testid="fc-drill-ask-copilot" onClick={() => { onClose(); askCopilot(`Explain "${line.label}" (${line.display}) from Forecast / Prediction Models and what I should do about it.`, line.copilot_context); }}
                className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-sm border border-primary/50 text-primary text-[12.5px] font-medium hover:bg-primary/10 transition-colors">
          <MessageSquare size={13} />Ask Copilot
        </button>
      </div>
    </>
  );
}

// Right-hand drill-down for any indicator, driver, chart or ranked line; full-screen on phones.
export default function DrillDrawer({ lineId, onClose }) {
  const [state, setState] = useState({ line: null, error: null });
  useEffect(() => {
    if (!lineId) return undefined;
    let alive = true;
    setState({ line: null, error: null });
    getLine(lineId).then(line => alive && setState({ line, error: null })).catch(e => alive && setState({ line: null, error: e.message }));
    const esc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    return () => { alive = false; window.removeEventListener("keydown", esc); };
  }, [lineId, onClose]);
  return createPortal(
    <div className="fixed inset-0 z-[60]" data-testid="fc-drill">
      <div className="absolute inset-0 bg-black/30 animate-fade-in" onClick={onClose} data-testid="fc-drill-backdrop" />
      <aside data-testid={`fc-drill-${testid(lineId)}`}
             className="absolute right-0 top-0 h-full w-full sm:w-[600px] bg-bg border-l border-border shadow-2xl overflow-y-auto fc-slide-in">
        {state.line ? <Body line={state.line} onClose={onClose} /> : (
          <div className="p-6 text-sm">
            <button data-testid="fc-drill-close" onClick={onClose} aria-label="Close" className="float-right h-8 w-8 inline-flex items-center justify-center border border-border rounded-sm"><X size={15} /></button>
            {state.error ? <div className="text-critical" data-testid="fc-drill-error">{state.error}</div>
              : <div className="text-muted animate-pulse" data-testid="fc-drill-loading">Opening…</div>}
          </div>
        )}
      </aside>
    </div>,
    document.body,
  );
}
